import React from 'react';
import { Typography, Box } from '@mui/material';
import { motion } from 'framer-motion';

const ACCENT = '#2563EB';

const jobs = [
  {
    title: 'Amazon Web Services (AWS)',
    role: 'Software Development Engineer',
    period: 'October 2024 – Present',
    details: [
      'Full-time SDE working on AWS Elastic Container Service (ECS/Fargate).',
      'Designed and deployed a distributed MCP server for ECS troubleshooting agents, enabling secure access to production data via multi-region routing; implemented defense-in-depth controls (IAM authorization, policy-enforced tool invocation, principal-aware rate limiting) and led threat modeling reviewed with AWS security engineers.',
      'Analyzed large-scale ECS usage data to design fraud-aware resource allocation policies, balancing fraud opex reduction and customer impact under adversarial usage patterns; led staged rollout in real-time provisioning systems.',
      'Led automation improvements for region builds, serving as primary owner for 4 region launches, reducing manual intervention by ~50% and enabling reliable deployment across complex infrastructure workflows.',
      'Defined system design and mentored an intern to build distributed metrics jobs computing region-wide vCPU utilization and risk-segmented capacity signals for detecting fraud and anomalous usage patterns.',
    ],
  },
  {
    title: 'Meta',
    role: 'Software Engineering Intern',
    period: 'May 2022 – August 2022',
    details: [
      'Designed data pipelines for ML-based ranking and recommendation systems.',
      'Engineered features for the ranking model, handling event logging and integration with a feature store.',
      'Enabled ranking in an unranked product area and created the production-ready execution graph.',
      'Conducted A/B testing to identify statistically significant changes in user engagement.',
    ],
  },
  {
    title: 'UC Berkeley',
    role: 'Academic Student Employee',
    period: 'August 2021 - December 2021 and August 2022 - December 2022',
    description: 'Course staff for the UC Berkeley CS course: <a href="https://cs61a.org/" target="_blank" rel="noopener noreferrer">CS 61A</a>.',
    details: [
      'Taught weekly sections and held regular office hours.',
      'Developed anti-cheat software that successfully identified and prevented academic dishonesty.',
      'Graded exams, reviewed course content, lead exam prep sessions.',
    ],
  },
  {
    title: 'Ayurdata',
    role: 'Co-founder & VP of Products',
    description: 'Founder project @ UC Berkeley',
    details: [
      'Built a feature-complete EHR prototype on top of <a href="https://openmrs.org/" target="_blank" rel="noopener noreferrer">OpenMRS</a>.',
      'Published <a href="https://www.npmjs.com/package/@ayurdata/esm-ayurdata-app" target="_blank" rel="noopener noreferrer">an npm package</a> with 60+ downloads.',
      'Developed AI tools: natural language document search, contextual patient summaries, and speech transcription pre-fill.',
      'Received 1M+ compute credits from Microsoft for Startups, GCP Startup Program, and Nvidia Inception.',
    ],
  },
];

const linkSx = {
  '& a': { color: ACCENT, '&:hover': { color: '#1D4ED8' } },
};

function Work() {
  return (
    <section id="work" style={{ paddingTop: '80px' }}>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.5 }}
      >
        <Typography variant="h4" component="h2" gutterBottom>
          Work Experience
        </Typography>
      </motion.div>

      <Box>
        {jobs.map((job, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: index * 0.08 }}
          >
            <Box sx={{ display: 'flex', gap: 3 }}>
              {/* Timeline column */}
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '20px', flexShrink: 0 }}>
                <Box sx={{
                  width: 12,
                  height: 12,
                  borderRadius: '50%',
                  bgcolor: ACCENT,
                  flexShrink: 0,
                  mt: '6px',
                  boxShadow: `0 0 0 3px white, 0 0 0 5px ${ACCENT}`,
                }} />
                {index < jobs.length - 1 && (
                  <Box sx={{ width: 2, flexGrow: 1, bgcolor: '#e0e0e0', mt: 1 }} />
                )}
              </Box>

              {/* Content column */}
              <Box sx={{ pb: 5, flex: 1, minWidth: 0, textAlign: 'left' }}>
                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 2, flexWrap: 'wrap', mb: 0.25 }}>
                  <Typography variant="h5" component="h3" sx={{ fontFamily: 'Poppins, sans-serif' }}>
                    {job.title}
                  </Typography>
                  {job.period && (
                    <Typography variant="caption" color="textSecondary">
                      {job.period}
                    </Typography>
                  )}
                </Box>

                <Typography variant="subtitle2" sx={{ color: ACCENT, fontWeight: 600, mb: job.description ? 0.5 : 1.5 }}>
                  {job.role}
                </Typography>

                {job.description && (
                  <Typography
                    variant="body2"
                    color="textSecondary"
                    component="div"
                    dangerouslySetInnerHTML={{ __html: job.description }}
                    sx={{ mb: 1.5, ...linkSx }}
                  />
                )}

                <ul style={{ paddingLeft: '20px', margin: 0 }}>
                  {job.details.map((detail, idx) => (
                    <li key={idx}>
                      <Typography
                        variant="body2"
                        color="textSecondary"
                        component="div"
                        dangerouslySetInnerHTML={{ __html: detail }}
                        sx={{ mb: 0.75, ...linkSx }}
                      />
                    </li>
                  ))}
                </ul>
              </Box>
            </Box>
          </motion.div>
        ))}
      </Box>
    </section>
  );
}

export default Work;
